import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { Crosshair } from "@/components/Crosshair";
import { RefreshCw, Store, Activity, Package, ShoppingCart } from "lucide-react";
import MarketplaceTab from "@/components/analytics/MarketplaceTab";
import TrafficTab from "@/components/analytics/TrafficTab";
import ListingsTab from "@/components/analytics/ListingsTab";
import AbandonedTab from "@/components/analytics/AbandonedTab";

const TABS = [
  { key: "marketplace", label: "Marketplace", icon: Store },
  { key: "traffic", label: "Traffic", icon: Activity },
  { key: "listings", label: "Listings", icon: Package },
  { key: "abandoned", label: "Abandoned", icon: ShoppingCart },
];

export default function AdminAnalytics() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("marketplace");
  const [force, setForce] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  if (loading || user === null) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdminRole(user.role)) return <Navigate to="/feed" replace />;

  const refresh = () => {
    setRefreshing(true);
    setForce((f) => f + 1);
    setTimeout(() => setRefreshing(false), 800);
  };

  return (
    <div className="text-foreground">
      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="admin-analytics-title">Command Analytics</h1>
            <p className="text-xs font-mono uppercase text-muted-foreground mt-1">
              Admin console · first-party event tracking (not GA4) · payment-dependent tiles gated until Stripe is wired
            </p>
          </div>
          <button onClick={refresh} data-testid="analytics-refresh" className="inline-flex items-center gap-2 border border-sniper hover:border-olive text-foreground font-mono uppercase text-sm px-4 py-2.5 transition-colors">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Force Refresh
          </button>
        </div>

        <Crosshair className="my-6" />

        {/* sub-tabs */}
        <div className="inline-flex border border-sniper bg-surface p-1 mb-6" data-testid="analytics-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`analytics-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-6 py-2.5 font-mono uppercase text-xs tracking-wide transition-colors ${tab === t.key ? "bg-olive text-white" : "text-secondary-foreground hover:text-white"}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "marketplace" && <MarketplaceTab force={force} />}
        {tab === "traffic" && <TrafficTab force={force} />}
        {tab === "listings" && <ListingsTab force={force} />}
        {tab === "abandoned" && <AbandonedTab force={force} />}
      </div>
    </div>
  );
}
